// src/lib/conference/audio-mix.ts
// Host-Seite: jeden eingehenden Gäste-Track per eigenem <audio>-Element + setSinkId auf das
// "Gäste-Return Output"-Device geben → Nuendo nimmt das als Input-Kanal (Muteomatic greift beim Record).
//
// BEWUSST KEIN WebAudio-Mix: das frühere `MediaStreamSource → Gain → MediaStreamDestination → <audio>
// → setSinkId` re-pufferte den Stream und kostete ~0,5 s Extra-Latenz (mouth-to-Nuendo gemessen 760 ms
// vs. ~200 ms L16). Schlichte <audio>-Elemente spielen den Track direkt aus — das OS summiert ohnehin
// alles, was auf dasselbe Output-Device geht, also bekommt Nuendo die Summe. Das spart die Latenz UND
// umgeht den Chrome/Edge-Remote-Track-zu-WebAudio-Stille-Bug (ein <audio> konsumiert den Track direkt).
// Master-Gain auf die Gäste brauchen wir nicht — Pegel/Mix macht Nuendo (Variante B).
type SinkAudio = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };

export class GuestReturnMix {
  private deviceId = '';
  private els = new Map<string, HTMLAudioElement>();

  /** Output-Device wählen (deviceId aus dem Cockpit-Dropdown). Auf alle bestehenden Elemente anwenden. */
  async setOutputDevice(deviceId: string): Promise<void> {
    this.deviceId = deviceId;
    for (const el of this.els.values()) await this.applySink(el);
  }

  private async applySink(el: HTMLAudioElement): Promise<void> {
    const e = el as SinkAudio;
    if (e.setSinkId && this.deviceId) { try { await e.setSinkId(this.deviceId); } catch { /* unsupported/Safari */ } }
    await el.play().catch(() => { /* autoplay-gate; greift nach dem ersten User-Gesture */ });
  }

  addTrack(key: string, track: MediaStreamTrack): void {
    if (this.els.has(key)) return;
    const el = new Audio();
    el.autoplay = true;
    el.srcObject = new MediaStream([track]);
    this.els.set(key, el);
    void this.applySink(el);   // → Return-Device → Nuendo
  }

  removeTrack(key: string): void {
    const el = this.els.get(key);
    if (el) { try { el.srcObject = null; } catch { /* ignore */ } this.els.delete(key); }
  }

  close(): void {
    for (const el of this.els.values()) { try { el.srcObject = null; } catch { /* ignore */ } }
    this.els.clear();
  }
}
